import React, { useEffect, useId, useRef, useState } from "react";
import {
  Info,
  HelpCircle,
  X,
  ChevronDown,
  ChevronRight,
  ExternalLink
} from "./icons";

/**
 * ContextualHelpDropdown - Warm Precision design-system primitive for the
 * "quick contextual explanation" pattern that was independently copy-pasted
 * (useState + GhostButton + hardcoded static <div>) across 5 places in the
 * app: KB topics, KB settings, Bot Profiles, NetSuite OAuth2 connect, and
 * Page Widgets. Use this instead of hand-rolling the same
 * useState/GhostButton/toggle pattern again.
 *
 * Renders an inline disclosure panel (NOT a floating <Popover> overlay) -
 * the content pushes the rest of the page down, matching the accordion-style
 * UX all 5 original call sites already used. For a floating/anchored menu,
 * use <Popover> instead; this component intentionally does not portal.
 *
 * Two content shapes:
 *   - Inline explanation: `title` + `body` (string or ReactNode).
 *   - Deep link to a help.velaro.com article: pass `articleUrl` (or
 *     `articleSlug`, composed onto the help center base URL) to render a
 *     "Learn more" link at the bottom of the panel. Optional - omit for a
 *     pure inline explanation.
 *
 * Pattern:
 *   <ContextualHelpDropdown
 *     title="How topics and categories work"
 *     body={<>...</>}
 *   />
 *
 *   <ContextualHelpDropdown
 *     title="How bot personas work"
 *     body={<HelpSectionsGrid />}
 *     articleSlug="bot-personas"
 *   />
 */

export const HELP_CENTER_BASE_URL = "https://help.velaro.com";

type Variant = "text" | "icon";

export interface ContextualHelpDropdownProps {
  /** Heading shown in the trigger (variant="text") and used as the trigger's aria-label. */
  title: string;
  /** Panel content - string or arbitrary ReactNode (lists, grids, code blocks, etc). */
  body: React.ReactNode;
  /**
   * Deep link to a specific help.velaro.com article, rendered as a
   * "Learn more" link at the bottom of the panel. Absolute URL - takes
   * priority over `articleSlug` if both are given.
   */
  articleUrl?: string;
  /** Slug composed onto `HELP_CENTER_BASE_URL` (e.g. "bot-personas" -> https://help.velaro.com/bot-personas). Ignored if `articleUrl` is set. */
  articleSlug?: string;
  /** Text for the deep-link. Default "Learn more". */
  learnMoreLabel?: string;
  /**
   * "text" (default) - GhostButton-style trigger with an Info icon, label,
   * and chevron; matches the KB topics/settings/Page Widgets call sites.
   * "icon" - icon-only trigger (HelpCircle / X swap) for compact/hero
   * placements; matches the Bot Profiles hero header call site. `title` is
   * still required and used as the aria-label.
   */
  variant?: Variant;
  /** Override the visible trigger text (variant="text" only). Defaults to `title`. */
  triggerLabel?: React.ReactNode;
  /** Start open. Default false. */
  defaultOpen?: boolean;
  /** Controlled open state. If provided, the component no longer manages its own state. */
  open?: boolean;
  /** Called whenever open state should change - required if `open` is controlled. */
  onOpenChange?: (open: boolean) => void;
  className?: string;
  triggerClassName?: string;
  panelClassName?: string;
  /** Render only the trigger button; caller is responsible for rendering the panel elsewhere (controlled open/onOpenChange required). */
  hideTrigger?: boolean;
  /** Render only the panel (assumes open is controlled and true); caller renders its own trigger elsewhere. */
  hidePanel?: boolean;
}

export default function ContextualHelpDropdown({
  title,
  body,
  articleUrl,
  articleSlug,
  learnMoreLabel = "Learn more",
  variant = "text",
  triggerLabel,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  className = "",
  triggerClassName = "",
  panelClassName = "",
  hideTrigger = false,
  hidePanel = false
}: ContextualHelpDropdownProps) {
  const [openState, setOpenState] = useState(defaultOpen);
  const open = openProp ?? openState;
  const setOpen = (next: boolean) => {
    if (onOpenChange) onOpenChange(next);
    if (openProp === undefined) setOpenState(next);
  };

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const panelId = useId();

  // Esc closes and returns focus to the trigger.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Click outside closes (no auto-refocus - matches natural disclosure behavior).
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const resolvedArticleUrl =
    articleUrl ??
    (articleSlug
      ? `${HELP_CENTER_BASE_URL}/${articleSlug.replace(/^\/+/, "")}`
      : undefined);

  return (
    <div className={className}>
      {!hideTrigger &&
        (variant === "icon" ? (
          <button
            ref={triggerRef}
            type="button"
            aria-haspopup="true"
            aria-expanded={open}
            aria-controls={panelId}
            aria-label={open ? `Close ${title}` : title}
            onClick={() => setOpen(!open)}
            className={`inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-(--ds-primary) ${triggerClassName}`}
          >
            {open ? (
              <X className="w-4 h-4" />
            ) : (
              <HelpCircle className="w-4 h-4" />
            )}
          </button>
        ) : (
          <button
            ref={triggerRef}
            type="button"
            aria-haspopup="true"
            aria-expanded={open}
            aria-controls={panelId}
            aria-label={title}
            onClick={() => setOpen(!open)}
            className={`inline-flex items-center gap-1.5 text-(--ds-primary) text-sm font-medium hover:underline transition-all disabled:opacity-50 disabled:cursor-not-allowed ${triggerClassName}`}
          >
            {open ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5" />
            )}
            <Info className="w-4 h-4" />
            {triggerLabel ?? title}
          </button>
        ))}

      {!hidePanel && open && (
        <div
          ref={panelRef}
          id={panelId}
          role="region"
          aria-label={title}
          className={`mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-zinc-700 space-y-2 ${panelClassName}`}
        >
          {typeof body === "string" ? <p>{body}</p> : body}
          {resolvedArticleUrl && (
            <p className="pt-1">
              <a
                href={resolvedArticleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-(--ds-primary) font-medium hover:underline"
              >
                {learnMoreLabel}
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
